import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Loader2, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';
import { useSubscription } from '@/hooks/useSubscription';
import InkPhDLogo from '@/components/InkPhDLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;

/** Post-registration onboarding: explain the trial, let the user pick a plan now. */
export default function WelcomePage() {
  const navigate = useNavigate();
  const user = authStorage.getAuthorUser();
  const { sub } = useSubscription();
  const [plans, setPlans] = useState([]);
  const [interval, setIntervalKey] = useState('monthly');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!authStorage.isAuthorAuthenticated()) { navigate('/thesis/login'); return; }
    // Fire-and-forget: the demo project is ready by the time they reach the dashboard
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/thesis/demo-project`, null,
      { params: { user_id: user?.id } }).catch(() => {});
    axios.get(`${API}/plans`)
      .then(({ data }) => setPlans((data.plans || []).filter((p) => p.key !== 'free')))
      .catch(() => toast.error('Could not load the plans'));
  }, [navigate, user?.id]);

  const subscribe = async (planKey) => {
    setBusy(planKey);
    try {
      const { data } = await axios.post(`${API}/checkout`, { plan: planKey, interval },
        { params: { user_id: user.id } });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || 'Checkout failed');
      setBusy('');
    }
  };

  const days = sub?.trial?.days_left ?? 14;

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0e1a] text-foreground thesis-brand">
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <InkPhDLogo size="sm" />
          <Link to="/thesis" className="text-[12px] text-muted-foreground hover:text-foreground underline"
            data-testid="welcome-skip-link">
            Skip for now
          </Link>        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16 space-y-8" data-testid="welcome-page">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/50
            bg-amber-500/[0.08] px-3 py-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300 mb-5"
            data-testid="welcome-trial-badge">
            <Clock className="w-3 h-3" /> Your {days}-day Doctoral trial is active
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif font-semibold tracking-tight mb-4"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            Welcome to <span className="text-[#1d4ed8] dark:text-[#7aa9ff]">InkPhD</span>
            {user?.first_name ? `, ${user.first_name}` : ''}
          </h1>
          <p className="text-base text-muted-foreground">
            For the next {days} days you have the full <strong>Doctoral</strong> feature set —
            unlimited projects, 600 AI generations a month, defence simulation, readiness scoring
            and DOCX / PDF / LaTeX export. No card, no charge. When the trial ends your account
            moves to the free Starter plan and every manuscript stays exactly where it is.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Button onClick={() => navigate('/thesis')} data-testid="welcome-continue-trial-btn"
              className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full">
              Continue with my free trial <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button asChild variant="outline" className="rounded-full" data-testid="welcome-guide-btn">
              <Link to="/guide">Read the step-by-step guide</Link>
            </Button>
            <div className="flex gap-2" data-testid="welcome-interval-toggle">
              {['monthly', 'yearly'].map((iv) => (
                <Button key={iv} variant={interval === iv ? 'secondary' : 'ghost'} size="sm"
                  className="rounded-full" onClick={() => setIntervalKey(iv)}
                  data-testid={`welcome-interval-${iv}`}>
                  {iv === 'monthly' ? 'Monthly' : 'Yearly · 2 months free'}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-base md:text-lg font-semibold mb-1">Or choose a plan now</h2>
          <p className="text-sm text-muted-foreground mb-5">
            Subscribing immediately keeps your access uninterrupted after the trial. You can also
            decide later from <Link to="/thesis/billing" className="underline">Plans &amp; billing</Link>.
          </p>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="welcome-plans">
            {plans.length === 0 ? (
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            ) : plans.map((p) => (
              <Card key={p.key} className={p.badge ? 'border-[#1d4ed8]/50 shadow-lg' : ''}
                data-testid={`welcome-plan-${p.key}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base">{p.name}</CardTitle>
                    {p.badge && <Badge className="text-[10px]">{p.badge}</Badge>}
                  </div>
                  <div className="mt-2">
                    <span className="text-3xl font-semibold">€{interval === 'monthly' ? p.monthly : p.yearly}</span>
                    <span className="text-xs text-muted-foreground ml-1">
                      {interval === 'monthly' ? '/ month' : '/ year'}
                    </span>
                  </div>
                  <CardDescription className="mt-1">{p.tagline}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ul className="space-y-1.5">
                    {(p.features || []).slice(0, 4).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-3.5 h-3.5 mt-1 text-[#1d4ed8] shrink-0" /><span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full rounded-full" variant={p.badge ? 'default' : 'outline'}
                    disabled={busy === p.key} onClick={() => subscribe(p.key)}
                    data-testid={`welcome-subscribe-${p.key}`}>
                    {busy === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : `Choose ${p.name}`}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
